import { Theme } from "@mui/material/styles";
import tokens from "./tokens";
import { SHADOW_LG } from "./Shadows";
import radii from "./radii";
import palette from "./palette";
import { DISPLAY_FONT } from "./Typography";

/**
 * The focus ring around an inline link. Traces a line of text, not a box, so
 * it stays under the control radius — at 6 the corners of a one-line target
 * bow.
 */
const FOCUS_RING_RADIUS = 4;

/**
 * Text fields stay at 44 — Professional doesn't restate this height on its
 * own; the field geometry (`MuiOutlinedInput`'s 11px padding plus 15px text)
 * lands here without anyone pinning it.
 *
 * Buttons still take no fixed height at all — a pill is sized by its padding,
 * so `MuiButton` states padding and lets the height fall out of it.
 */
const CONTROL_HEIGHT = 44;

/** Icon-button sizes: 40 hairline (overflow), 46 filled (row action), 56 FAB (card primary). */
const ICON_BUTTON_SIZE = 46;
const ICON_BUTTON_SIZE_SMALL = 40;
const ICON_BUTTON_SIZE_FAB = 56;

/**
 * Professional's button lift is a neutral flat shadow, not a coloured glow.
 * The glow was Playful's signature and is explicitly retired: "Pill buttons,
 * flat — no colored glow." One pair for every colour, so `accent`, `primary`
 * and `error` no longer each need their own.
 */
const BUTTON_LIFT = "0 1px 2px rgba(28, 38, 40, 0.14)";
const BUTTON_LIFT_HOVER = "0 2px 6px rgba(28, 38, 40, 0.18)";

/**
 * Two surface tints the design system names that no non-`theme/` call site
 * uses, so they are module constants here rather than `tokens.ts` entries —
 * that file's contract is one token per real, grepped call site. The
 * "Selected" tint pair used to sit here too, as its own literal hexes; it
 * doesn't any more — `palette.tint.selected` (bound to `TINTS` below) is
 * both its single definition and, via `Chips.stories.tsx`/`Tints.stories.tsx`
 * enumerating `theme.palette.tint`, already read by non-`theme/` call sites,
 * so restating it here would have been the second copy, not the first.
 */
/** A disabled contained button's fill. */
const DISABLED_FILL = "#DAD7D0";
/** The rule inside cards and menus — a step darker than the card hairline. */
const DIVIDER_LINE = "#D6D6D6";

/** Disabled field fill. Unchanged — Professional specifies the same tone. */
const FIELD_DISABLED_FILL = "#E7E5E0";
/** Inset panel / dialog footer. Unchanged. */
const INSET_PANEL = "#F4F3F0";
/** A disabled checkbox's border. */
const CHECKBOX_DISABLED_BORDER = "#D6D6D6";
/**
 * The glyph inside a hairline icon button. A step darker than `text.secondary`
 * (#5C6667): at 40px the icon has to hold its own against the 2px border
 * around it rather than recede the way secondary *text* does.
 */
const ICON_BUTTON_GLYPH = "#4A5556";

/**
 * The status tint pairs, shared by chips and alerts. Read off the `./palette`
 * module rather than `theme.palette.tint`: MUI's `createPalette` deep-clones
 * any custom plain-object palette key with no built-in default slot, so
 * `theme.palette.tint` is a value-identical but reference-distinct copy of
 * `palette.tint`. Reading the module keeps `palette.ts` visibly the single
 * definition. (Arrays are not cloned this way, which is why
 * `theme.palette.avatarHues` *is* reference-identical — do not generalise
 * from that to object-valued slots.)
 */
const TINTS = palette.tint;

/** A tint pair as an `Alert` severity slot: tinted panel, matching left rule. */
const alertTint = ({ bg, fg }: { bg: string; fg: string }) => ({
  backgroundColor: bg,
  color: fg,
  borderLeftColor: fg,
  "& .MuiAlert-icon": { color: fg },
});

/**
 * The rich-text editor, platform-wide — Playful's editor as drawn in the
 * Builder Experience sheet's answer step.
 *
 * There is one editor design in this app and it lives here. Quill ships its own
 * skin in `src/FormInputs/styles/quill.snow.css` (1,134 lines of vendor Snow),
 * and every screen that mounted an editor used to re-dress it locally on top of
 * that: an sx block in `BuildersQuillForm`, another in the Hub Library cards,
 * another on the project profile. Snow stays — it carries the *mechanics*
 * (picker behaviour, icon paths, tooltip positioning) — but the appearance is
 * stated once, right here, so a change to the editor is a change to the theme.
 *
 * Snow is imported by a component, so it lands in the stylesheet after
 * `CssBaseline` and wins any specificity tie. Every selector below therefore
 * carries `.ql-snow` (or its own compound) to out-specify snow's `.ql-*.ql-snow`
 * rules rather than relying on order.
 *
 * The shape: one bordered, clipped box; a tinted toolbar strip across its top
 * with a hairline under it; the writing surface below at reading size.
 */
const quillSnowStyles = (theme: Theme) => {
  const { ladder } = tokens.designSystem;

  return {
    /**
     * The bordered box. `.quill` is react-quill's wrapper, which is what owns
     * the outline — snow put a border on the toolbar and on the container
     * separately, which drew two stacked boxes rather than one.
     *
     * Scoped to wrappers that actually have a toolbar. The same component
     * renders read-only prose all over this app (`modules={{ toolbar: false }}`
     * for a topic's brief, a builder's intro, a project's About), and an
     * unscoped rule drew a control's outline around body copy that is not a
     * control.
     */
    ".quill:has(> .ql-toolbar)": {
      border: `1px solid ${ladder.controlLine}`,
      borderRadius: `${radii.sm}px`,
      overflow: "hidden",
      backgroundColor: theme.palette.common.white,
    },
    ".ql-toolbar.ql-snow": {
      border: 0,
      borderBottom: `1px solid ${ladder.divider}`,
      backgroundColor: ladder.insetPanel,
      padding: tokens.space.quillToolbarPadding,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "4px",
      fontFamily: "inherit",
      // Snow spaces its groups with margins; the sheet spaces the whole row
      // with one gap, so the groups give that control up.
      "& .ql-formats": {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        margin: 0,
        // Snow's own clearfix for its float-based layout (`.ql-formats:after
        // { content:''; display:table }`) is dead weight now that this is a
        // flex container — worse, a generated `::after` box is itself a flex
        // item unless hidden, so left alone it sat in the row as a phantom
        // item eating space the real buttons needed to wrap cleanly.
        "&::after": { content: "none", display: "none" },
        // The warm rule between groups, which snow has no equivalent of.
        "&:not(:last-of-type)": {
          paddingRight: "10px",
          marginRight: "6px",
          borderRight: `1px solid ${ladder.ruleWarm}`,
        },
      },
      "& button, & .ql-picker-label": {
        color: ladder.glyph,
        // `theme.shape.control`, not `radii.chip`: Task 2 retargeted
        // `radii.chip` to 999 (Professional draws chips as pills), which
        // would draw these toolbar buttons as circles instead of rounded
        // squares.
        borderRadius: `${theme.shape.control}px`,
      },
      "& button": {
        width: 28,
        height: 28,
        padding: "4px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        float: "none",
        "&:hover": { backgroundColor: theme.palette.common.white },
      },
      // Snow colours its glyphs by stroking/filling the inline SVGs, so the
      // `color` above does not reach them — they are addressed directly.
      "& .ql-stroke": { stroke: ladder.glyph },
      "& .ql-fill": { fill: ladder.glyph },
      "& button:hover .ql-stroke, & button.ql-active .ql-stroke": {
        stroke: theme.palette.primary.main,
      },
      "& button:hover .ql-fill, & button.ql-active .ql-fill": {
        fill: theme.palette.primary.main,
      },
      "& button.ql-active": { backgroundColor: theme.palette.common.white },
      // The heading picker, drawn as the sheet's small white pill.
      "& .ql-picker": {
        height: "auto",
        fontSize: tokens.fontSize.quillToolbar,
        fontWeight: 700,
        color: ladder.glyph,
      },
      "& .ql-picker-label": {
        padding: "4px 10px",
        border: `1px solid ${ladder.ruleWarm}`,
        backgroundColor: theme.palette.common.white,
        display: "inline-flex",
        alignItems: "center",
      },
      "& .ql-picker-options": {
        borderRadius: `${radii.md}px`,
        border: `1px solid ${ladder.divider}`,
        boxShadow: theme.shadows[2],
      },
    },
    // The writing surface: no second border, and body copy at reading size.
    ".ql-container.ql-snow": {
      border: 0,
      fontFamily: "inherit",
      fontSize: tokens.fontSize.quillEditorBody,
      color: theme.palette.text.primary,
      /**
       * Snow's own `.ql-editor h1/h2/h3 strong/em/u` rules (quill.snow.css
       * ~lines 87-129) each set their own `font-family: 'Lato'`, so this
       * object's own `fontFamily: "inherit"` above never reaches them:
       * specificity only arbitrates between rules matching the *same*
       * element, and `inherit` on the container is merely the fallback a
       * descendant falls back to when nothing more specific claims it —
       * Snow's rules do claim it. Plain heading text inherits the theme
       * font already; a bold, italic or underlined run inside a heading
       * does not, regardless.
       *
       * `.ql-container.ql-snow` (2 classes) plus `.ql-editor` (1 class) plus
       * two element selectors out-specifies Snow's own `.ql-editor h1
       * strong` (1 class, 2 elements) outright, so this wins independent of
       * load order — the same reason every other selector in this function
       * carries the `.ql-snow` compound.
       */
      "& .ql-editor h1 strong, & .ql-editor h2 strong, & .ql-editor h3 strong, & .ql-editor h1 em, & .ql-editor h2 em, & .ql-editor h3 em, & .ql-editor h1 u, & .ql-editor h2 u, & .ql-editor h3 u":
        { fontFamily: "inherit" },
    },
    /**
     * The link/video tooltip's border. Snow's own rule (quill.snow.css
     * ~lines 807-844, duplicated per mode and again inside each mode's
     * `@media (max-width:619px)` block) hardcodes `border: 2px solid
     * #0094a4` — Playful's `primary.main`, not Professional's. Only the
     * colour is restated here (as `borderColor`, not the full shorthand):
     * `bottom`/`position` are Snow's positioning mechanics, unrelated to
     * the theme, and stay Snow's to own.
     *
     * `.ql-container.ql-snow` (2 classes) plus `.ql-tooltip` (1 class) plus
     * the attribute selector out-specifies Snow's `.ql-snow .ql-tooltip[…]`
     * (1 class, 1 class, 1 attribute) at every viewport width, so one
     * unconditional rule here beats both the base and the media-query
     * copies without needing a media query of its own.
     */
    ".ql-container.ql-snow .ql-tooltip[data-mode='link'], .ql-container.ql-snow .ql-tooltip[data-mode='video']":
      { borderColor: theme.palette.primary.main },
    ".ql-editor": {
      lineHeight: 1.65,
      "&.ql-blank::before": {
        color: theme.palette.text.disabled,
        fontStyle: "normal",
        left: 20,
        right: 20,
      },
    },
    /**
     * The writing surface's inset, scoped the same way as the box. Read-only
     * prose is inset by whatever card it sits in (`padding: 2` on the Hub
     * Library cards and the project About, nothing on a topic's body), so the
     * theme leaves it alone rather than overriding those from a distance.
     */
    ".quill:has(> .ql-toolbar) .ql-editor": { padding: "18px 20px" },
  };
};

const getThemedComponents = (
  theme: Theme
): { components: Theme["components"] } => {
  /**
   * Professional's shape roles: buttons and chips are pills, inputs and
   * controls take 6, cards take 8, dialogs take 10. Read straight off the
   * `theme` this function already receives — `theme.shape` is their single
   * definition (set in `Theme-variable.tsx`, typed by `mui.d.ts`'s `Shape`
   * augmentation) — rather than restated as module literals here.
   *
   * The multiplication trap they're re-bound to a unit string to avoid is
   * specific to `theme.unstable_sx`/`sx` contexts: those MULTIPLY a bare
   * numeric `borderRadius` by `shape.borderRadius` exactly as a call site's
   * `sx` does — a bare `CONTROL_RADIUS` inside one would render inputs at
   * 48px. Plain `styleOverrides` objects (not wrapped in `unstable_sx`) don't
   * do this multiplication — `MuiSwitch.track`'s bare `borderRadius:
   * PILL_RADIUS` below is harmless for exactly that reason — but every use
   * here is still interpolated into a `${…}px` string for consistency.
   */
  const {
    control: CONTROL_RADIUS,
    card: CARD_RADIUS,
    dialog: DIALOG_RADIUS,
    pill: PILL_RADIUS,
  } = theme.shape;
  return {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            boxSizing: "border-box",
          },
          html: {
            height: "100%",
            width: "100%",
            fontFamily: "'Lato', sans-serif",
            overflowX: "hidden",
          },
          body: {
            height: "100vh",
            minHeight: "100vh",
            margin: 0,
            padding: 0,
            fontFamily: "'Lato', sans-serif",
            overflowX: "hidden",
            position: "relative",
            // The app's own scrollbar, from _layout.scss. Webkit-only there too.
            "&::-webkit-scrollbar": { width: "0.5em", height: "0.5em" },
            /**
             * Ladder roles: the thumb is `controlLine` (grey.400 — the same
             * tone a field's outline takes, which is what a scrollbar thumb
             * is), and the track is the inset-panel tone behind it. These were
             * grey.600/grey.400 when grey.600 was a cool #707070 doing both the
             * glyph and the scrollbar job.
             */
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.grey[400],
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.grey[200],
            },
          },
          "#root": {
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "100vh",
          },
          ".MuiCardHeader-action": {
            alignSelf: "center !important",
          },
          p: {
            fontFamily: "'Lato', sans-serif",
            margin: "0 0 1em",
            lineHeight: 1.4,
            // Load-bearing: the bare <p> that render user-written text rely on
            // this to keep the newlines the author typed.
            whiteSpace: "pre-wrap",
          },
          "ul, ol": { listStyleType: "none", padding: 0, margin: 0 },
          img: { fontSize: "0.5em", color: theme.palette.secondary.main },
          /**
           * Wave 13 moved the Sass global layer here. Everything below styles
           * an element that is deliberately *not* a MUI component — react-router
           * renders a bare <a>, react-confirm-alert renders bare <button>s, and
           * a handful of forms still use native controls. Each rule keeps the
           * `:not(.Mui…)` guard it had in _base.scss so MUI's own components are
           * untouched; the hexes are theme values now rather than $variables.
           */
          "h1:not(.MuiTypography-root)": {
            fontFamily: DISPLAY_FONT,
            fontSize: "3.2em",
            fontWeight: 500,
            letterSpacing: "1px",
            lineHeight: 1.4,
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h2:not(.MuiTypography-root)": {
            fontFamily: DISPLAY_FONT,
            fontSize: "1.5em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            lineHeight: 1.4,
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h3:not(.MuiTypography-root)": {
            fontSize: "1.2em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            lineHeight: 1.4,
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h4:not(.MuiTypography-root)": {
            fontSize: "1.1em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h5:not(.MuiTypography-root)": {
            fontSize: "1em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h6:not(.MuiTypography-root)": {
            fontFamily: DISPLAY_FONT,
            display: "block",
            fontSize: "0.9em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            textTransform: "uppercase",
            color: theme.palette.grey[600],
            margin: 0,
          },
          /*
            `:not(.MuiButtonBase-root)` keeps this off Buttons and IconButtons
            rendered as anchors via `component={Link}`. The selector is (0,2,1)
            and an `sx` class is (0,1,0), so without the exclusion this rule
            outranks anything a call site writes — call sites were reaching for
            `"&&&"` just to get their own colour back, and none of them beat the
            `:hover` branch, which repainted a contained button's label
            `secondary.main` (near-black) on its own dark fill. Buttons take
            their colour from their variant now.
          */
          "a:not(.MuiMenuItem-root):not(.MuiLink-root):not(.MuiButtonBase-root)":
          {
            color: tokens.color.accentTeal,
            textDecoration: "none",
            cursor: "pointer",
            lineHeight: "110%",
            "&:hover": { color: theme.palette.secondary.main },
            "&:focus": {
              borderBottom: `2px solid ${tokens.color.accentTealBright}`,
              outline: "none",
            },
          },
          "button:not(.MuiButtonBase-root, .MuiIconButton-root, .MuiButton-root, .MuiCardActionArea-root)":
          {
            backgroundColor: "transparent",
            letterSpacing: "0.7px",
            cursor: "pointer",
            borderRadius: 3,
            boxSizing: "border-box",
            fontSize: "0.8em",
            "&:focus": {
              borderBottom: `2px solid ${tokens.color.accentTealBright}`,
              outline: "none",
            },
            "&:disabled, &:disabled:hover": {
              color: theme.palette.grey[600],
              backgroundColor: theme.palette.grey[50],
              borderColor: theme.palette.grey[300],
              cursor: "not-allowed",
            },
          },
          "input:not(.MuiInputBase-input), textarea:not(.MuiInputBase-input)": {
            border: `0.5px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.common.white,
            color: theme.palette.common.black,
            fontFamily: "'Lato', sans-serif",
            fontWeight: 400,
            letterSpacing: "1px",
            lineHeight: "1.5em",
            padding: "10px 6px",
            boxSizing: "border-box",
            cursor: "text",
            // 16px keeps iOS Safari from zooming the viewport on focus.
            fontSize: "16px !important",
            "&::placeholder": { color: theme.palette.grey[800] },
            "&:focus": {
              outline: 0,
              borderBottom: `3px solid ${theme.palette.primary.main}`,
            },
            "&:disabled": {
              backgroundColor: theme.palette.grey[300],
              color: theme.palette.grey[600],
              cursor: "not-allowed",
            },
          },
          "textarea:not(.MuiInputBase-input)": {
            whiteSpace: "pre-line",
            overflow: "auto",
            margin: "5px auto",
          },
          select: {
            fontFamily: "'Lato', sans-serif",
            fontWeight: 400,
            letterSpacing: "1px",
            lineHeight: "1.5em",
            border: `0.5px solid ${theme.palette.grey[300]}`,
            borderRadius: 0,
            WebkitAppearance: "none",
            cursor: "pointer",
            color: theme.palette.common.black,
            padding: "10px 20px 10px 6px",
            margin: "5px auto",
            boxSizing: "border-box",
            background: `${theme.palette.common.white} url(/images/top_dropdown.png) no-repeat 95% center`,
            backgroundSize: "10px 5px",
            "&:disabled": { backgroundImage: "none" },
            "&::-ms-expand": { display: "none" },
            "&:focus": {
              outline: 0,
              borderBottom: `3px solid ${theme.palette.primary.main}`,
              fontSize: "16px !important",
            },
          },
          "label:not(.MuiInputLabel-root)": {
            marginTop: 8,
            marginBottom: 4,
            lineHeight: "110%",
            cursor: "pointer",
          },
          "input:-webkit-autofill": {
            transition: "background-color 5000s ease-in-out 0s",
          },
          /**
           * react-confirm-alert injects its own markup with its own class names,
           * so these never appeared in any `className` in the repo and every
           * class-usage sweep read them as dead. They are not: six components
           * call `confirmAlert()`.
           */
          ".react-confirm-alert-overlay": {
            zIndex: "999 !important",
            transform: "translateZ(1000px)",
          },
          ".react-confirm-alert-body": {
            borderRadius: "0 !important",
            fontFamily: "'Lato', sans-serif !important",
            h1: { textTransform: "uppercase" },
          },
          ".react-confirm-alert-button-group": {
            justifyContent: "flex-end !important",
            button: {
              borderRadius: "3px !important",
              "&:first-of-type": {
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.secondary.main}`,
                color: theme.palette.secondary.main,
              },
              "&:last-child": {
                background: tokens.color.accentTeal,
                border: `1px solid ${tokens.color.accentTeal}`,
              },
            },
          },
          "&.MuiTypography-root": {
            padding: 0,
            border: 0,
            outline: 0,
            verticalAlign: "baseline",
            fontStyle: "normal",
          },
          "&.MuiTypography-root.capitalize": {
            textTransform: "capitalize",
          },
          ...quillSnowStyles(theme),
        },
      },
      /**
       * Every flex row in Playful's sheet spaces its children with `gap`, and
       * pushes a trailing group over with `justify-content` — never with
       * margins. MUI's default `Stack` does the opposite: `spacing` compiles to
       * `margin-left`/`margin-top` on each child plus a blanket
       * `& > :not(style):not(style) { margin: 0 }` reset ("relies on each child
       * to give up control of the margin", per its source).
       *
       * That reset is a class plus two type selectors — specificity (0,1,2) —
       * so it outranks a child's own `sx` class (0,1,0). Every `ml: "auto"` on
       * a Stack child in this app (47 of them) was therefore a no-op, which is
       * why rows meant to split — a back link left, a "N sections left" +
       * primary pill right — rendered clustered against the left edge.
       *
       * `useFlexGap` switches `spacing` to real `gap`, which needs no reset, so
       * child margins take effect again everywhere at once. It also fixes the
       * wrapped-row case those rows hit at narrow widths: margin-based spacing
       * indents the second line, `gap` does not.
       */
      MuiStack: {
        defaultProps: { useFlexGap: true },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: "0px",
            paddingRight: "0px",
            paddingTop: "0px",
          },
        },
      },
      /**
       * The top bar stays WHITE with a hairline bottom rule.
       *
       * The design system's own `themeCode` sets `MuiAppBar` to `#222F30`,
       * commented "Dark app bar / sidebar surface" — but that describes the
       * sidebar, which its mockups build as a plain `div`, never an `AppBar`.
       * Its colour table assigns `background.paper` (#FFFFFF) to "Cards, top
       * bar" and `neutral.dark` (#222F30) to "Sidebar, app header band", and
       * §08 specifies the top bar as "64px white, 1px #DEDEDE bottom rule".
       * This app's only `AppBar` is `AppLayout/partials/header/Header.jsx` —
       * the top bar — so taking that override literally would paint the one
       * surface the doc says must stay white.
       */
      MuiAppBar: {
        styleOverrides: {
          root: theme.unstable_sx({
            backgroundColor: "common.white",
            borderBottom: "1px solid",
            borderColor: "divider",
          }),
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            paddingLeft: "0 !important",
            paddingRight: "0 !important",
          },
        },
      },
      /**
       * The app's modals render through Dialog. Only the backdrop tint is
       * app-specific; the centring, white surface, radius and max-height that
       * a modal needs are all Dialog defaults, so they are not repeated here.
       *
       * Scoped through the Dialog root rather than `MuiBackdrop` so Drawer,
       * Menu and Popover keep the standard MUI scrim, and through
       * `styleOverrides` rather than `defaultProps.slotProps` so a call site
       * passing its own `slotProps.backdrop` does not drop the tint.
       */
      MuiDialog: {
        styleOverrides: {
          root: {
            "& .MuiBackdrop-root": {
              backgroundColor: tokens.color.modalOverlay,
            },
          },
          /**
           * Professional dialogs: 10px radius, a hairline outline in
           * `DIVIDER_LINE`, and a shadow one step lighter than `SHADOW_LG`
           * (0.14 alpha, not `SHADOW_LG`'s 0.18) — the doc states this value
           * for the dialog specifically, distinct from the floating-surface
           * tier `SHADOW_LG` covers (menus, popovers). Written longhand
           * rather than as `SHADOW_LG` so a later pass does not "simplify"
           * it back to the wrong tier.
           *
           * The border is kept because the doc specifies it (line 495), not
           * because it is doing edge-visibility work here: the backdrop
           * behind this paper is `tokens.color.modalOverlay` —
           * `secondary.main` at 90% opacity, a nearly opaque dark teal, not
           * a light scrim — so a white paper's edges already read clearly
           * against it without a border. The doc doesn't state a reason for
           * the border beyond specifying it, so none is invented here.
           */
          paper: theme.unstable_sx({
            borderRadius: `${DIALOG_RADIUS}px`,
            boxShadow: "0 14px 34px rgba(28, 38, 40, 0.14)",
            border: "1px solid",
            borderColor: DIVIDER_LINE,
          }),
        },
      },
      /**
       * A dialog's action row is an inset footer — `#F4F3F0` from the ladder,
       * divided from the body by `DIVIDER_LINE` (`#D6D6D6` — not warm;
       * `LINE_WARM` was retired in Task 5), actions right-aligned with a
       * 12px gap (doc lines 446, 498). The same treatment the assembled form
       * card uses, so both read as one pattern.
       */
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: INSET_PANEL,
            borderTop: `1px solid ${DIVIDER_LINE}`,
            padding: "14px 24px",
            gap: 12,
          },
        },
      },
      /**
       * Tooltip specification can be found in below link. Should be applied for all components
       * @reference
       *  - https://rebel-base.atlassian.net/wiki/spaces/REBELBASE/pages/23232513/Rebel+Dashboard+Reference+codes
       */
      MuiTooltip: {
        styleOverrides: {
          /**
           * `borderRadius` is an explicit px string, not the bare `1` this
           * carried. A bare number here is a `shape.borderRadius` multiplier,
           * so it rendered 5px only because that was the old scalar — raising
           * the scalar to Playful's card radius would have silently doubled it.
           * A tooltip is a small floating control, so it takes the control
           * radius outright.
           */
          tooltip: theme.unstable_sx({
            p: 2,
            maxWidth: 256,
            /**
             * The ladder's darkest body tone (grey.800, #2A3237) rather than
             * the glyph tone: a tooltip is a filled dark surface carrying white
             * text, so it needs the contrast end of the ramp.
             */
            background: theme?.palette?.grey["800"],
            borderRadius: `${CONTROL_RADIUS}px`,
          }),
        },
      },
      MuiButton: {
        /**
         * MUI's own grey elevation shadow is disabled here because the flat
         * `BUTTON_LIFT` below already supplies a contained button's shadow —
         * the two stacked would muddy it.
         */
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: ({ ownerState }) => {
            /**
             * Playful sizes a pill by its padding rather than a fixed height,
             * so the height each size used to pin (56/48/40) is gone and falls
             * out of padding plus line-height instead.
             *
             * `minWidth` stays, at pill-appropriate values (the pre-Playful
             * 248/174/102 were much larger). A pill with no minimum collapses
             * around a one-word label and reads as a chip.
             */
            const state = {
              minWidth: 160,
              padding: "13px 28px",
              fontSize: "15.5px",
              borderRadius: `${PILL_RADIUS}px`,
            };
            if (ownerState.size === "small") {
              state.minWidth = 88;
              state.padding = "6px 16px";
              state.fontSize = "12.5px";
            } else if (ownerState.size === "medium") {
              state.minWidth = 120;
              state.padding = "10px 22px";
              state.fontSize = "14px";
            }
            /**
             * An outlined button carries a 2px border the other variants don't,
             * so its padding comes down by 2px on each axis to keep the outer
             * box identical — the design system pairs contained `10px 22px`
             * with outlined `8px 20px` for exactly this reason. Without it, an
             * outlined button beside a contained one sits 4px taller and wider.
             */
            if (ownerState.variant === "outlined") {
              const [y, x] = state.padding
                .split(" ")
                .map((v) => Number.parseFloat(v) - 2);
              state.padding = `${y}px ${x}px`;
            }
            return theme.unstable_sx(state);
          },
          /**
           * Playful's outlined border is 2px, and stays 2px on hover — MUI's
           * own default thickens from 1px only while hovered, which shifts the
           * label by half a pixel.
           *
           * The hover inversion to filled is a per-colour variant below, not
           * here: it needs both the fill and the label colour, and
           * `backgroundColor: currentColor` cannot express it — `currentColor`
           * resolves against the `color` set in the same rule, so the fill would
           * come out the same white as the label.
           */
          outlined: { borderWidth: 2, "&:hover": { borderWidth: 2 } },
          /**
           * A text button is a quiet control, and the one button variant that
           * is NOT on the display face: every text button in the design system
           * ("Cancel", "Keep track") carries no `font-family` and sits at
           * weight 700, i.e. Lato. `typography.button` puts Manrope 600 on all
           * buttons, so this has to opt back out — otherwise the escape-hatch
           * action reads as loudly as the primary one.
           */
          text: theme.unstable_sx({
            fontFamily: theme.typography.fontFamily,
            color: "text.secondary",
            fontWeight: 700,
            /**
             * And it is not a pill, so it takes none of the pill geometry the
             * root sets. The sheet draws every text action as bare words with a
             * touch target around them (`padding: 9px 4px`) — the root's
             * `minWidth: 160` plus `13px 28px` put ~90px of dead space between
             * "← back" and the action next to it, which is what made those
             * footer rows read as three unrelated controls.
             */
            minWidth: 0,
            padding: "9px 4px",
            fontSize: "14px",
          }),
        },
        variants: [
          /**
           * Professional's contained button is flat: one neutral lift for every
           * colour, and no vertical travel on hover. Playful gave each colour
           * its own glow and lifted the button 1px — the glow is explicitly
           * retired ("Pill buttons, flat — no colored glow") and the travel went
           * with it, since a shadow that grows without the button moving reads
           * as the same affordance.
           */
          {
            props: { variant: "contained" },
            style: {
              boxShadow: BUTTON_LIFT,
              "&:hover": { boxShadow: BUTTON_LIFT_HOVER },
              /**
               * Disabled contained buttons take a warm, explicit fill rather
               * than MUI's cool `action.disabledBackground`, and drop the
               * lift entirely — a disabled control that still floats reads
               * as pressable.
               */
              "&.Mui-disabled": {
                backgroundColor: DISABLED_FILL,
                color: theme.palette.text.disabled,
                boxShadow: "none",
              },
            },
          },
          /**
           * `error.main` is the lighter alert red, so a destructive contained
           * button overrides the fill itself, not just the lift.
           */
          {
            props: { variant: "contained", color: "error" },
            style: {
              backgroundColor: theme.palette.error.dark,
              "&:hover": { backgroundColor: theme.palette.error.dark },
            },
          },
          /**
           * "Outlined buttons invert to filled" on hover (§08's hover rules),
           * rather than taking MUI's translucent tint. One variant per colour
           * because the inversion needs that colour's own fill AND a label
           * colour that holds contrast against it.
           *
           * `error` and `warning` can't just take `.main` + `contrastText`
           * the way the rest do: white on `error.main` (`#E57373`) is
           * ~2.99:1, and white on `warning.main` (`#D98E12`) is ~2.68:1 — the
           * worst pairing in the system. Both get an exception below.
           *
           * `error` inverts to `error.dark` instead, the same red the
           * contained variant above refuses `error.main` for — white on
           * `error.dark` is ~6.67:1.
           *
           * `warning.dark` does not fix `warning` the same way: white on it
           * is only ~4.34:1, still short of the 4.5:1 body-text minimum.
           * `warning` instead keeps its fill at `warning.main` and darkens
           * the label to `text.primary` — ~6.17:1 — rather than inverting to
           * a different fill, since inverting doesn't clear the bar here.
           */
          ...(
            ["primary", "secondary", "error", "success", "warning", "info", "accent"] as const
          ).map((color) => {
            const fill =
              color === "error" ? theme.palette.error.dark : theme.palette[color].main;
            const label =
              color === "warning" ? theme.palette.text.primary : theme.palette[color].contrastText;
            return {
              props: { variant: "outlined" as const, color },
              style: {
                "&:hover": {
                  backgroundColor: fill,
                  color: label,
                },
              },
            };
          }),
        ],
      },
      /**
       * The 56px minimum this forced on Select menu rows is gone: Playful's
       * menu row is a compact 9/12 pill (see `MuiMenuItem` below), and a
       * Select's dropdown is the same menu as every other. Forcing it taller
       * only for Selects made one menu in the app look unlike the rest.
       *
       * The closed control keeps the field padding, so a Select and a text
       * input of the same size are the same height.
       */
      MuiSelect: {
        styleOverrides: {
          select: { padding: "11px 13px" },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const state = {
              /**
               * 320 forced every field to a fixed width regardless of its
               * container. Professional forms stack a label above a field that
               * fills the column, so the minimum comes down to something that
               * keeps a lone input from collapsing without dictating layout.
               */
              minHeight: CONTROL_HEIGHT,
              minWidth: 240,
            };
            if (ownerState.size === "small") {
              state.minHeight = 36;
              state.minWidth = 128;
            }
            return theme.unstable_sx({
              ...state,
              "&.MuiOutlinedInput-root": {
                backgroundColor: "common.white",
                /**
                 * The resting border colour and the radius live on
                 * `MuiOutlinedInput`'s own `root`/`notchedOutline` below; this
                 * owns only the states that layer a different colour/width on
                 * top of that resting border. Focus takes the primary teal at
                 * 2px (MUI's own `.Mui-focused .notchedOutline` already widens
                 * to 2px; only the colour needs setting here) with no outer
                 * ring — `Login Reskin.dc.html` shows a plain 2px border,
                 * nothing outside it.
                 */
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme?.palette?.grey?.[500],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                /**
                 * Error state: 1.5px `error.light`, not MUI's default (1px
                 * `error.main`) — the label pairs with `error.main` itself
                 * (see `Field.tsx`'s `&.Mui-error` rule).
                 */
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1.5px",
                  borderColor: "error.light",
                },
                /**
                 * A disabled field is filled, not merely greyed: the design
                 * system gives it the `#E7E5E0` ladder fill with a
                 * step-darker border than the resting one, so "you cannot
                 * edit this" reads without depending on the text colour
                 * alone.
                 */
                "&.Mui-disabled": {
                  backgroundColor: FIELD_DISABLED_FILL,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: DIVIDER_LINE,
                  },
                },
              },
            });
          },
        },
      },
      /**
       * Every field in the app renders its label *above* the control in normal
       * document flow (see `components/FormInput/Field.tsx`), so `TextField`
       * never receives a `label` and there is no notch to cut. The notch
       * geometry MUI leaves behind is still live CSS, though — a 1px gap in the
       * top border where the legend would sit, and an outline offset up by half
       * a label's height — so it is suppressed here rather than left to render
       * an empty cut-out: `top: 0` closes the offset, and hiding the legend
       * closes the gap itself.
       *
       * The resting border is `grey[400]` (`#B0ADA4`) — Professional's input
       * border, per the ladder's `controlLine` role (`palette.ts`). An earlier
       * comment here called that "a cool tone" and reached for a separate
       * `tokens.color.inputBorder` token instead; that was true against
       * Playful's `grey[400]`, but the palette retarget landed `grey[400]` on
       * this exact warm tone, so the token and the palette slot are now the
       * same colour and this points at the palette slot directly rather than
       * the token.
       *
       * `input` padding and font size come from the field spec; the
       * `MuiInputBase` override above owns the height and the border-colour
       * states (hover/focus/error/disabled) that layer on top of this resting
       * border, which are shared with the non-outlined bases.
       */
      MuiOutlinedInput: {
        styleOverrides: {
          root: theme.unstable_sx({ borderRadius: `${CONTROL_RADIUS}px` }),
          notchedOutline: {
            top: 0,
            borderColor: theme.palette.grey[400],
            legend: { display: "none" },
          },
          input: { padding: "11px 13px", fontSize: "15px" },
        },
      },
      /** Playful's forms are full-width and outlined; every call site asked for
       * this explicitly before it moved here. */
      MuiTextField: {
        defaultProps: { variant: "outlined", size: "medium", fullWidth: true },
      },
      /**
       * The label-above-field treatment, hoisted out of the ~16 call sites that
       * repeated it inline (`Field.tsx`, `SignInForm.tsx`, `ResetPassword.tsx`,
       * and so on). `shrink` stops MUI trying to float the label; `position`
       * and `transform` undo the absolute placement and 0.75 scale that go with
       * floating.
       *
       * No `marginBottom`: `Field.tsx` already spaces the label from its field
       * with the flex container's `gap`, and both would render double.
       */
      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: theme.unstable_sx({
            position: "static",
            transform: "none",
            typography: "label",
            color: "text.primary",
            "&.Mui-focused": { color: "text.primary" },
            "&.Mui-error": { color: "error.main" },
            /** A disabled field's label greys out with it (doc line 360). */
            "&.Mui-disabled": { color: "text.disabled" },
          }),
        },
      },
      /**
       * MUI indents helper text to clear the floating label's box. With labels
       * above the field there is nothing to clear, so it aligns with the field
       * itself. The size comes from `typography.caption`, which
       * `FormHelperText` spreads for itself.
       */
      MuiFormHelperText: {
        styleOverrides: {
          root: { marginLeft: 0 },
        },
      },
      /**
       * Checked is the primary teal; unchecked and disabled take ladder tones.
       *
       * The design system draws its checkbox as a 19px square with an 8px
       * radius. That is NOT reachable from the theme: MUI renders the box as an
       * SVG glyph (`CheckBoxOutlineBlankIcon`), whose corner radius is part of
       * the path, so only replacing the `icon`/`checkedIcon` components can
       * change the shape. Colour is what the theme can own, and is what this
       * sets; the radius is left for whoever swaps the icons.
       */
      MuiCheckbox: {
        styleOverrides: {
          root: theme.unstable_sx({
            color: "text.disabled",
            "&.Mui-checked": { color: "primary.main" },
            "&.Mui-disabled": { color: CHECKBOX_DISABLED_BORDER },
          }),
        },
      },
      /**
       * Skeletons take `DIVIDER_LINE` (`#D6D6D6`) rather than MUI's cool grey
       * wash, and the control radius, not the card radius. Almost every call
       * site (`ActivityTileSkeleton`, `MemberCard`,
       * `BuilderSchedulePlaceholder`, and the rest) stands a bare
       * `text`/`rectangular` bar in for a 15-20px line of copy — a shape
       * that reads as a small control, not a card. `CarouselSkeleton` is the
       * one exception, sizing a `rectangular` skeleton to the full
       * `DashboardCard` footprint (224×276) rather than a line inside one; at
       * that size 6px vs. 8px of corner is not a visible difference, so it
       * doesn't cost the majority case anything to lose the tie there. A
       * skeleton stands in for what it's loading, and for every call site
       * but that one, that's text and rows, so the control radius is what
       * this takes. `DIVIDER_LINE` is not a warm tone (`LINE_WARM` was
       * retired in Task 5); it is simply the rule tone one step darker than
       * `divider`.
       *
       * `MuiSkeleton` defaults to a `::after` wave animation over
       * `backgroundColor`; setting the colour here keeps that animation and only
       * changes the base tone.
       */
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: DIVIDER_LINE,
            borderRadius: `${CONTROL_RADIUS}px`,
          },
        },
      },
      /**
       * A 38×20 track with a 16px thumb, per the design system — considerably
       * tighter than MUI's default 58×38 with its 20px thumb and 12px of
       * padding, so every part has to be restated: the root's box, the
       * `switchBase` inset that positions the thumb, and the distance it
       * travels when checked (38 − 16 − 2×2 padding = 18px).
       */
      MuiSwitch: {
        styleOverrides: {
          root: { width: 38, height: 20, padding: 0, display: "flex" },
          switchBase: theme.unstable_sx({
            padding: "2px",
            "&.Mui-checked": {
              transform: "translateX(18px)",
              color: "common.white",
              "& + .MuiSwitch-track": {
                backgroundColor: "primary.main",
                opacity: 1,
              },
            },
          }),
          thumb: theme.unstable_sx({
            width: 16,
            height: 16,
            boxShadow: "none",
            color: "common.white",
          }),
          track: {
            borderRadius: PILL_RADIUS,
            backgroundColor: tokens.color.inputBorder,
            opacity: 1,
          },
        },
      },
      /**
       * A 4px rail in the divider line tone with a teal fill and a 16px
       * `primary.dark` thumb (doc lines 406-408). MUI's default thumb is 20px
       * and carries a focus halo sized to it, so the halo's box-shadow spread
       * comes down with the thumb.
       */
      MuiSlider: {
        styleOverrides: {
          root: { height: 4 },
          rail: { backgroundColor: DIVIDER_LINE, opacity: 1 },
          track: { border: "none" },
          thumb: theme.unstable_sx({
            width: 16,
            height: 16,
            backgroundColor: "primary.dark",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: `0 0 0 6px ${theme.palette.primary.main}29`,
            },
            "&.Mui-active": {
              boxShadow: `0 0 0 10px ${theme.palette.primary.main}29`,
            },
          }),
        },
      },
      /**
       * `borderRadius: "9px"` was a bare Playful literal outside the constant
       * system — Task 5 flagged it as the old `sm` radius surviving by
       * dead-reckoning. Folded onto `CONTROL_RADIUS` here rather than kept as
       * its own value: this slot carries the rail's spacing, not a distinct
       * shape role.
       */
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: `${CONTROL_RADIUS}px`,
          },
        },
      },
      /**
       * The navigation row is a full pill, and the selected one is filled
       * solid `primary.main` rather than MUI's default translucent
       * `action.selected` wash. These rows render on both white card
       * surfaces (`BuilderTopicSideNav`'s `Card`) and the neutral page
       * ground (`HubActivityNav`'s filter list sits on `background.default`,
       * not a card) — the doc's own words are "filled primary.main on
       * white", but a solid fill reads clearly against either ground, which
       * a low-opacity overlay would not: it reads as a faint smudge rather
       * than a clear "you are here" marker.
       *
       * The hover value is `TINTS.selected.fg` (`#00707D`) — the "Selected"
       * tint pair's foreground, a named token in its own right rather than
       * drift off `primary.dark` (`#005E69`).
       */
      MuiListItemButton: {
        styleOverrides: {
          root: theme.unstable_sx({
            borderRadius: `${PILL_RADIUS}px`,
            minHeight: 40,
            padding: "10px 12px",
            fontWeight: 700,
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              color: "common.white",
              fontWeight: 700,
              "&:hover": { backgroundColor: TINTS.selected.fg },
            },
          }),
        },
      },
      /**
       * Professional cards: white on the neutral `#ECECEC` page ground, a
       * hairline border in `divider`, and the neutral medium shadow
       * (`boxShadow: 2` resolves to `SHADOW_MD` — see `Shadows.tsx`). The
       * border is what stops a white card dissolving into a light
       * background; the shadow lifts it.
       */
      MuiCard: {
        styleOverrides: {
          root: theme.unstable_sx({
            padding: 0,
            margin: 0,
            borderRadius: `${CARD_RADIUS}px`,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 2,
          }),
        },
      },
      MuiList: {
        styleOverrides: {
          root: theme.unstable_sx({
            p: theme.spacing(0),
          }),
        },
      },
      /**
       * 28, down from 40: Playful's nav rows sit the icon close to its label,
       * and `color: inherit` is what lets a selected row's white text carry its
       * icon with it — MUI's default pins `action.active`, so the icon stayed
       * dark grey on the filled teal row above.
       */
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 28,
            color: "inherit",
          },
        },
      },
      /**
       * Playful's section label inside a nav list: a small, wide-tracked,
       * uppercase eyebrow on no background. `background: transparent` is the
       * load-bearing part — MUI defaults a subheader to `background.paper`, an
       * opaque white band that the `MuiPaper` override makes visible against
       * the sidebar.
       */
      MuiListSubheader: {
        styleOverrides: {
          root: theme.unstable_sx({
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "1.6px",
            textTransform: "uppercase",
            color: "text.disabled",
            background: "transparent",
            lineHeight: 2.2,
          }),
        },
      },
      /**
       * Overlapping avatars — a participant row, a member stack — ring each
       * face in white, which is the surface they sit on in every sheet that
       * draws them. MUI's default ring is `background.default`: on this palette
       * that is the neutral soft grey page ground (#ECECEC), so on a white
       * card the rings read as grey gaps between the faces rather than as
       * separation.
       */
      MuiAvatarGroup: {
        styleOverrides: {
          avatar: theme.unstable_sx({ borderColor: "common.white" }),
        },
      },
      /** `DIVIDER_LINE` (`#D6D6D6`) — a step darker than `palette.divider`
       * (`#DEDEDE`), which is the card hairline; this is the rule *inside* a
       * surface, so it needs to read against a white background rather than
       * the page ground. */
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: DIVIDER_LINE },
        },
      },
      MuiTabs: {
        styleOverrides: {
          /** Playful's indicator is a 3px teal rule — MUI's default is 2px in
           * `primary.main`, so only the thickness actually changes. */
          indicator: theme.unstable_sx({
            height: 3,
            backgroundColor: "primary.main",
          }),
          root: {
            "&.rebel-disabled": {
              "& .MuiTabs-indicator": {
                backgroundColor: theme?.palette?.action?.disabledBackground,
              },
              "& .Mui-selected": {
                color: theme?.palette?.action?.disabled,
              },
            },
          },
        },
      },
      /**
       * Playful tabs are underline style with the primary colour on the active
       * tab — which is MUI's default indicator, so only the proportions and the
       * active colour need stating. 80px tall was a Sass-era holdover; 56 was
       * an intermediate step, and Playful's own row is 44.
       *
       * The resting colour is `text.secondary`, not `grey[400]` — an inactive
       * tab is secondary *text*, and `grey[400]` (`#B0ADA4`) is the
       * scrollbar-thumb / control-line grey, a shade this palette reserves
       * for controls rather than text. (This used to be `grey[600]`, when
       * `grey[600]` was Playful's cool #707070 doing both jobs — the ramp
       * remap moved the control-line role to `grey[400]`, so the comparison
       * has to move with it.)
       */
      MuiTab: {
        styleOverrides: {
          root: theme.unstable_sx({
            textTransform: "none",
            color: "text.secondary",
            fontWeight: 700,
            fontSize: "15px",
            minHeight: 44,
            minWidth: 96,
            "&.Mui-selected": { color: "primary.main" },
          }),
        },
      },
      /**
       * The progress bar is a thick, filled pill, not a hairline — it reads
       * as a "how far along is this project" indicator with a label beside
       * it, not a loading spinner's cousin. 28px tall and fully rounded; the
       * value is unchanged from Playful's own bar. A call site that wants a
       * thin indeterminate strip instead of this filled track needs its own
       * height override — see the Task 10 report's LinearProgress survey.
       */
      MuiLinearProgress: {
        styleOverrides: {
          root: theme.unstable_sx({
            /**
             * `grey[200]` (`#F4F3F0`) — the ramp's inset-surface role — not
             * the bespoke `#ecf0f2` this carried: a cool light grey with no
             * Professional home, invisible to `check:visual-constants`
             * because that ratchet exempts `src/theme/`.
             */
            backgroundColor: "grey.200",
            borderRadius: `${PILL_RADIUS}px`,
            height: 28,
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            "&:last-child": {
              paddingBottom: "1rem",
            },
          },
        },
      },
      /**
       * A menu floats at elevation 8, which resolves to `SHADOW_LG` under
       * `Shadows.tsx`'s ramp — the floating-surface tier menus and popovers
       * share. Dialogs are NOT the same surface despite sitting in that same
       * conceptual tier: `MuiDialog` above pins a one-step-lighter literal of
       * its own (0.14 alpha, not `SHADOW_LG`'s 0.18) precisely so it reads as
       * distinct from a menu. The card hairline plus a little padding gives
       * the item pills below room to sit inside without touching the edge.
       * The list's own default padding is removed in favour of the paper's,
       * or the two stack.
       */
      MuiMenu: {
        defaultProps: { elevation: 8 },
        styleOverrides: {
          paper: theme.unstable_sx({
            borderRadius: `${DIALOG_RADIUS}px`,
            border: "1px solid",
            borderColor: "divider",
            padding: "6px",
            boxShadow: SHADOW_LG,
          }),
          list: { padding: 0 },
        },
      },
      /**
       * Playful's menu row is its own pill inside the menu's padding. The
       * selected row is a pale teal wash with teal text — it used to be a solid
       * `primary.main` fill with white text, which on a menu (as opposed to a
       * nav sidebar) reads as a pressed button rather than a current choice.
       */
      MuiMenuItem: {
        styleOverrides: {
          root: theme.unstable_sx({
            borderRadius: `${CONTROL_RADIUS}px`,
            padding: "9px 12px",
            fontSize: "14px",
            fontWeight: 700,
            gap: "11px",
            "&.Mui-selected": {
              backgroundColor: TINTS.selected.bg,
              color: TINTS.selected.fg,
            },
          }),
        },
      },
      /**
       * Playful gives icon-only controls three sizes with distinct jobs: 56 for
       * a card's primary action (a `Fab`), 46 for a row action, and 40 for an
       * overflow/secondary action, which is the only one that carries a resting
       * border. The border is what makes a bare icon read as a control at that
       * size — at 46 the filled background does that job instead.
       */
      MuiIconButton: {
        styleOverrides: {
          root: theme.unstable_sx({
            borderRadius: `${PILL_RADIUS}px`,
            width: ICON_BUTTON_SIZE,
            height: ICON_BUTTON_SIZE,
          }),
          sizeSmall: {
            width: ICON_BUTTON_SIZE_SMALL,
            height: ICON_BUTTON_SIZE_SMALL,
            color: ICON_BUTTON_GLYPH,
          },
          sizeLarge: {
            width: ICON_BUTTON_SIZE_FAB,
            height: ICON_BUTTON_SIZE_FAB,
          },
        },
      },
      /**
       * A `Fab` is the card's primary action: the deeper teal, not
       * `primary.main`, so it reads as one step above a contained button, with
       * the same flat lift that button carries.
       */
      MuiFab: {
        defaultProps: { size: "medium" },
        styleOverrides: {
          root: theme.unstable_sx({
            backgroundColor: "primary.dark",
            color: "common.white",
            boxShadow: BUTTON_LIFT,
            "&:hover": { backgroundColor: "primary.dark", boxShadow: BUTTON_LIFT_HOVER },
          }),
          sizeMedium: { width: 50, height: 50 },
        },
      },
      /**
       * A link is teal and unadorned until hovered, when it browns toward the
       * accent and underlines. `accent.dark` (`#8E3D15`) is that brown,
       * reached through the palette slot below rather than a hardcoded hex,
       * so a future retarget of `accent.dark` carries every link hover with
       * it.
       *
       * The focus ring is explicit because `underline: "none"` removes the only
       * other affordance a keyboard user would have had.
       */
      MuiLink: {
        defaultProps: { underline: "none", color: "primary" },
        styleOverrides: {
          root: theme.unstable_sx({
            cursor: "pointer",
            fontWeight: 700,
            "&:hover": {
              color: "accent.dark",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            },
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
              borderRadius: `${FOCUS_RING_RADIUS}px`,
            },
          }),
          underlineAlways: { textUnderlineOffset: 3 },
        },
        variants: [
          {
            props: { underline: "always" },
            style: { textDecorationColor: "currentColor" },
          },
          {
            props: { underline: "hover" },
            style: {
              "&:hover": { textDecorationColor: "currentColor" },
            },
          },
        ],
      },
      MuiPaper: {
        styleOverrides: {
          /**
           * `common.white`, not `background.paper` — the two are the same
           * value today, but pinning the literal is what keeps a Paper white
           * if `background.paper` ever moves. `background.paper` and the
           * page ground (`background.default`, `#ECECEC`) are different
           * values on purpose; a Paper that inherited the ground instead of
           * this pin would stop lifting off it.
           */
          root: theme.unstable_sx({
            backgroundColor: theme?.palette?.common?.white,
          }),
          /**
           * Every rounded surface carries the hairline and the card radius,
           * not just `Card`. A white Paper on the neutral `#ECECEC` page
           * ground has no edge of its own, so without this a Popover,
           * Accordion or bare `Paper` dissolves into the background at its
           * corners while a `Card` beside it does not.
           */
          rounded: theme.unstable_sx({
            border: "1px solid",
            borderColor: "divider",
            borderRadius: `${CARD_RADIUS}px`,
          }),
        },
      },
      /**
       * A chip is a pill, and a label rather than body copy: 12.5/700, where
       * `typography: body1` had it at 15/400 — the same size as the paragraph it
       * sat next to, which is what made chips read as inline text rather than as
       * tags.
       *
       * The radius is 999 (`PILL_RADIUS`), not the 6 of `CONTROL_RADIUS` —
       * `CONTROL_RADIUS` is the menu row and the alert; a chip is fully
       * rounded like the buttons it sits beside.
       */
      MuiChip: {
        styleOverrides: {
          root: theme.unstable_sx({
            borderRadius: `${PILL_RADIUS}px`,
            fontSize: "12.5px",
            fontWeight: 700,
            borderColor: theme?.palette?.grey["400"],
            boxShadow: 1,
            color: theme?.palette?.secondary?.main,
          }),
        },
        /**
         * The status tint pairs, one variant per MUI colour. A chip reports the
         * state of a record — it is tinted and non-interactive — so each colour
         * takes its pair's background and foreground together, never a filled
         * palette colour with white text.
         *
         * `default` is left alone: an untinted chip is the neutral case and
         * inherits the root styles above. The `archived` pair rides on
         * `color="secondary"` because MUI's `default` cannot be targeted by a
         * colour variant without also catching every unstyled chip.
         */
        variants: [
          ...(
            [
              ["success", TINTS.success],
              ["error", TINTS.error],
              ["warning", TINTS.warning],
              ["info", TINTS.info],
              // The three that are a domain role rather than a severity, so the
              // colour name and the tint name differ.
              ["secondary", TINTS.archived],
              ["primary", TINTS.selected],
              ["accent", TINTS.count],
            ] as const
          ).map(([color, tint]) => ({
            props: { color, variant: "filled" as const },
            style: {
              backgroundColor: tint.bg,
              color: tint.fg,
              "& .MuiChip-deleteIcon": { color: tint.fg },
            },
          })),
          /**
           * `variant="solid"` — the sheet's *other* chip. Playful draws a chip
           * two different ways and the difference is the fill, not the hue:
           *
           * - `filled` reports a record's **state**, tinted: pale background,
           *   dark foreground from the same pair ("6 critical" on #FAEAE8 over
           *   #A83226). That is the tint map above.
           * - `solid` **names what a thing is** — a step's kind badge ("INTRO",
           *   "STORY"), a concern's severity, the reviewer chip that is
           *   currently open. The sheet draws all three as the palette colour at
           *   full strength with a white label.
           *
           * Only the first had a theme expression, so six call sites hand-wrote
           * `sx={{ color: "common.white" }}` over a `filled` chip to get the
           * second. Once `filled` became tinted, every one of those turned into
           * white-on-pale and the label vanished. Stating `solid` once removes
           * the reason to reach for that `sx` at all.
           *
           * Geometry stays with the root — a solid chip is still a pill. Only
           * the fill and the label (icons included) change.
           */
          ...(
            [
              "primary",
              "secondary",
              "success",
              "error",
              "warning",
              "info",
              "accent",
            ] as const
          ).map((color) => ({
            props: { color, variant: "solid" as const },
            style: {
              backgroundColor: theme.palette[color].main,
              color: theme.palette[color].contrastText,
              fontFamily: DISPLAY_FONT,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "1.6px",
              textTransform: "uppercase",
              "& .MuiChip-icon, & .MuiChip-deleteIcon": {
                color: theme.palette[color].contrastText,
              },
              // `solid` names an active/current state (the selected filter
              // pill, the open reviewer chip) rather than an action still
              // waiting to be taken — hovering or focusing it should read as
              // inert, not invite another click by darkening. Stated equal
              // to the resting fill, not omitted, so MUI's own clickable
              // hover/focus darken-on-action.hover default can't show through.
              "&.MuiChip-clickable:hover, &.MuiChip-clickable:focus": {
                backgroundColor: theme.palette[color].main,
              },
            },
          })),
          /**
           * A `solid` chip with no colour. MUI's `default` has no palette entry
           * to take a fill from, so it takes the darkest surface the sheet gives
           * a chip-sized element.
           */
          {
            props: { variant: "solid" as const, color: "default" as const },
            style: {
              backgroundColor: theme.palette.neutral.dark,
              color: theme.palette.common.white,
              fontFamily: DISPLAY_FONT,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "1.6px",
              textTransform: "uppercase",
              "& .MuiChip-icon, & .MuiChip-deleteIcon": {
                color: theme.palette.common.white,
              },
              // Same rationale as the coloured pairs above: active, not
              // hover-reactive.
              "&.MuiChip-clickable:hover, &.MuiChip-clickable:focus": {
                backgroundColor: theme.palette.neutral.dark,
              },
            },
          },
        ],
      },
      /**
       * The filter pill (segmented filter) — one row of mutually exclusive
       * pills that narrows the list below it, each carrying its own count
       * ("All 26 · Active 7 · Inactive 19"). Exactly one is always selected.
       *
       * `ToggleButtonGroup` is the MUI component for this: mutually exclusive,
       * one always on. The group's default look is the opposite of a pill row —
       * it collapses its children into one bordered rectangle with shared
       * edges — so the group unsets that and spaces the pills 8px apart,
       * wrapping at narrow widths.
       *
       * `secondary.main` (#0D3D47) is the selected fill. The design system
       * reserves that tone for this component precisely so a selected filter
       * never reads as an outlined button, so the group deliberately does NOT
       * take `primary`.
       */
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { gap: 8, flexWrap: "wrap" },
          grouped: {
            border: "1px solid",
            borderRadius: `${PILL_RADIUS}px !important`,
            marginLeft: "0 !important",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: theme.unstable_sx({
            padding: "8px 16px",
            borderRadius: `${PILL_RADIUS}px`,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "13px",
            color: "text.secondary",
            backgroundColor: "common.white",
            borderColor: "divider",
            "&:hover": {
              backgroundColor: "common.white",
              borderColor: tokens.designSystem.filterPill.hoverLine,
              color: "text.primary",
            },
            "&.Mui-selected": {
              backgroundColor: "secondary.main",
              color: "common.white",
              borderColor: "secondary.main",
              boxShadow: 1,
              "&:hover": {
                backgroundColor: "secondary.main",
                borderColor: "secondary.main",
              },
            },
            /** An option with nothing behind it: filled, not merely faded. */
            "&.Mui-disabled": {
              backgroundColor: INSET_PANEL,
              color: tokens.color.inputBorder,
              borderColor: "divider",
            },
          }),
        },
      },
      /**
       * Tables: a tinted header band with uppercase micro-type, hairline
       * rules top and bottom, and a row hover one shade off white.
       *
       * The header tone is `grey[100]` (`#F4F4F4`) — the same tone the
       * input-prefix box takes (see `palette.ts`'s `grey` ramp comment), so
       * a table header band and a form's prefix box read as one tone across
       * the app.
       *
       * The design doc itself disagrees with itself here: its own table
       * markup (line 557) measures to `#E7E5E0` (Playful's disabled-field
       * fill), while its prose lists `#FAF6EE` for "table header" instead.
       * Neither survives into Professional — this plan resolves the
       * conflict by putting the table header on `grey[100]`, the same slot
       * the input-prefix box uses. Recorded here so a future reader does not
       * have to rediscover the doc's internal disagreement from scratch.
       */
      MuiTableHead: {
        styleOverrides: {
          root: theme.unstable_sx({
            backgroundColor: "grey.100",
            "& .MuiTableCell-head": {
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              color: "text.secondary",
              borderTop: `1px solid ${DIVIDER_LINE}`,
            },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: theme.unstable_sx({
            borderBottomColor: DIVIDER_LINE,
            color: "grey.600",
            fontSize: "14px",
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: tokens.designSystem.ladder.rowHover },
            /** The head row is a band, not a hoverable row. */
            "&.MuiTableRow-head:hover": { backgroundColor: "transparent" },
          },
        },
      },
      /**
       * A toast is a filled dark card at the card radius, carrying the one cool
       * shadow in the system — a warm tint on a near-black surface reads as a
       * smudge rather than a lift.
       */
      MuiSnackbarContent: {
        styleOverrides: {
          root: theme.unstable_sx({
            borderRadius: `${CARD_RADIUS}px`,
            backgroundColor: "text.primary",
            color: "common.white",
            fontWeight: 700,
            fontSize: "14px",
            boxShadow: tokens.designSystem.toast.shadow,
          }),
        },
      },
      /**
       * Toasts anchor bottom-left and clear themselves after five seconds.
       * Stated here because every `Snackbar` call site in the app wants the
       * same, and MUI's own default (bottom-centre, no auto-hide) is neither.
       */
      MuiSnackbar: {
        defaultProps: {
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          autoHideDuration: 5000,
        },
      },
      /**
       * Playful's alert is a tinted panel with a solid severity rule down its
       * leading edge, drawing on the same status tint pairs the chips use — so
       * a "Past due" alert and a "Past due" chip are the same two colours.
       *
       * MUI's `standard` variant computes its own tint by lightening
       * `palette[severity].main`, which lands near these pairs but not on them,
       * and gives the border the un-lightened `main` — hence each severity
       * restating background, text and rule together. The icon inherits the
       * rule colour, matching the doc's alert markup (line 482-483).
       *
       * Done as `variants` keyed on `severity` rather than through
       * `standardSuccess`-style slots: MUI 9 replaced those composite class
       * names with `standard` + `colorSuccess`, so the old slot names no longer
       * type-check (and would silently no-op if they did).
       */
      MuiAlert: {
        defaultProps: { variant: "standard" },
        styleOverrides: {
          root: {
            borderRadius: `${CONTROL_RADIUS}px`,
            borderLeftWidth: 4,
            borderLeftStyle: "solid",
          },
          filled: {
            borderRadius: `${CARD_RADIUS}px`,
            fontWeight: 700,
          },
        },
        variants: (
          [
            ["success", TINTS.success],
            ["error", TINTS.error],
            ["warning", TINTS.warning],
            ["info", TINTS.info],
          ] as const
        ).map(([severity, tint]) => ({
          props: { variant: "standard" as const, severity },
          style: alertTint(tint),
        })),
      },
    },
  };
};

export default getThemedComponents;
